/* ──────────────────────────────────────────────
 *  Finalisation (spec Parcours P3 § 7.6) : un seul bouton, choisi
 *  avant la pré-réservation d'après les conditions annoncées par le
 *  serveur (`cart-summary.checkout`) et le choix payer en ligne / sur
 *  place ; texte du bouton de paiement, mention de la validation par
 *  le pro, case d'engagement réservée aux cas avec pénalité.
 * ────────────────────────────────────────────── */

import { PrestationPaymentType, type CartSummaryCheckoutObject } from '@cfixe-monorepo/shared';
import type { BookingLabels, PreReservationResponse } from '../types';
import { hasNonZeroFormattedAmount } from './payment-amounts';

/** `pay_online` : la pré-réservation ouvre le paiement ; `confirm` / `request` : le même clic confirme. */
export type CheckoutFlow = 'pay_online' | 'confirm' | 'request';

/** Choix du client quand le pro laisse payer en ligne ou sur place (`FULL_PAYMENT_OPTIONAL`). */
export type CheckoutPayChoice = 'online' | 'on_site';

/* Empreinte et carte enregistrée : rien n'est débité, mais la carte se saisit en ligne. */
const CARD_GUARANTEES = new Set<string>([PrestationPaymentType.CARD_SAVE, PrestationPaymentType.BANK_IMPRINT]);

/** Le choix payer en ligne / sur place a un sens : paiement au choix du client et quelque chose à payer. */
export const offersPayChoice = (preview: CartSummaryCheckoutObject | undefined): boolean =>
  preview?.payment_type === PrestationPaymentType.FULL_PAYMENT_OPTIONAL && preview.amount_to_pay_today_raw > 0;

export function resolveCheckoutFlow(
  preview: CartSummaryCheckoutObject | undefined,
  choice: CheckoutPayChoice,
): CheckoutFlow | undefined {
  if (!preview) return undefined;
  const online = offersPayChoice(preview)
    ? choice === 'online'
    : CARD_GUARANTEES.has(preview.payment_type) ||
      (preview.payment_type !== PrestationPaymentType.FULL_PAYMENT_OPTIONAL && preview.amount_to_pay_today_raw > 0);
  if (online) return 'pay_online';
  return preview.requires_validation ? 'request' : 'confirm';
}

/**
 * Pré-réservation qui se confirme sans paiement en ligne : gratuite, ou rien à payer aujourd'hui hors empreinte
 * (même règle que l'écran de paiement). Paiement au choix : seulement quand le serveur annonce explicitement
 * 0 (carte cadeau, bon ou code qui couvre tout — le choix en ligne / sur place n'a alors plus d'objet) ;
 * montant inconnu, c'est l'écran de paiement qui tranche.
 */
export function needsNoOnlinePayment(data: PreReservationResponse): boolean {
  const type = data.payment_type;
  if (type === PrestationPaymentType.FREE) return true;
  if (type && CARD_GUARANTEES.has(type)) return false;
  const amountToPayToday = data.payment_infos?.amount_to_pay_today_raw;
  if (type === PrestationPaymentType.FULL_PAYMENT_OPTIONAL) return amountToPayToday === 0;
  return (amountToPayToday ?? 0) === 0;
}

/**
 * Paiement débité dès la validation (acompte, paiement complet, paiement en ligne choisi) : jamais une empreinte ni
 * une carte enregistrée, qui ne débitent rien aujourd'hui, ni un montant nul (carte cadeau qui couvre ce qui est dû).
 */
export const chargesOnlineNow = (paymentType: string | undefined, amountToPayTodayRaw: number | undefined): boolean =>
  !!paymentType &&
  paymentType !== PrestationPaymentType.FREE &&
  !CARD_GUARANTEES.has(paymentType) &&
  (amountToPayTodayRaw ?? 0) > 0;

/**
 * Mention de la validation par le pro (`confirm_every_booking`), au-dessus du bouton (spec Parcours § 7.6), quel que
 * soit le paiement. Un paiement en ligne est encaissé tout de suite, avant la réponse du pro : la mention le dit, sans
 * rien promettre d'un remboursement (s'il refuse, c'est lui qui en décide).
 */
export const validationNotice = (
  chargedNow: boolean,
  labels: Pick<BookingLabels, 'validation_notice' | 'validation_notice_charged_now'>,
): string =>
  chargedNow ? `${labels.validation_notice} ${labels.validation_notice_charged_now}` : labels.validation_notice;

/**
 * Bouton du bloc paiement (spec Parcours § 7.6) : « Payer 36,00 € », « Payer l’acompte 10,80 € ». Une empreinte ou une
 * carte enregistrée ne débite rien maintenant : pas de « Payer », mais « Confirmer le rendez-vous », ou « Envoyer ma
 * demande » quand le pro valide chaque réservation.
 */
export function paymentButtonLabel(
  payment: {
    paymentType: string | undefined;
    /** Montant du jour, formaté serveur. */
    amountToPay: string | undefined;
    amountToPayRaw: number | undefined;
    requiresValidation: boolean;
  },
  labels: Pick<BookingLabels, 'pay_amount' | 'pay_deposit_amount' | 'confirm_appointment' | 'send_request'>,
): string {
  const charged =
    payment.amountToPayRaw !== undefined ? payment.amountToPayRaw > 0 : hasNonZeroFormattedAmount(payment.amountToPay);
  if (!payment.amountToPay || !charged || !payment.paymentType || CARD_GUARANTEES.has(payment.paymentType)) {
    return payment.requiresValidation ? labels.send_request : labels.confirm_appointment;
  }
  const template =
    payment.paymentType === PrestationPaymentType.DEPOSIT ? labels.pay_deposit_amount : labels.pay_amount;
  return template.replace('{amount}', payment.amountToPay);
}

/**
 * Une pénalité existe : la case « Je m'engage à venir » s'affiche et bloque le bouton (spec Parcours § 7.6), et
 * seulement alors. Pénalité = frais d'annulation tardive annoncés par le serveur (acompte conservé, part non
 * remboursée, empreinte ou carte débitée), que le délai d'annulation gratuite soit à venir ou déjà dépassé (rendez-vous
 * du jour) ; ou une carte cadeau, un forfait ou un bon engagé, que l'annulation hors délai ne rend pas. Sans paiement
 * en ligne ni bon, rien n'est dû à l'annulation : pas de case.
 */
export const hasCancellationPenalty = (penalty: { cancellationFeeOutRaw?: number; hasVoucher: boolean }): boolean =>
  (penalty.cancellationFeeOutRaw ?? 0) > 0 || penalty.hasVoucher;
